const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/store/medication.ts';
import Imm from 'immutable';
import { KISIM_TIME_FORMAT } from '@cistec/common_web/defs/generalDefs';
import { isLoadingOrdersOfCurPat, mediOrdersOfCurPat } from '@cistec/common_web/selectors/orderSelectors';
import {
    createAdministrationTimes,
    isDeletedOrder,
    isPausedOrder,
} from '@cistec/common_web/utils/orderHotspotEventUtils';
import {
    buildRowsOfOrders,
    getHotspotsTimes,
    mapSubsequentOrders,
    sortOrdersBasic,
} from '@cistec/common_web/utils/orderUtils';
import { compareStringWNumber } from '@cistec/common_web/utils/stringUtils';
import moment, { Moment } from 'moment';
import {
    IAdministrationPoint,
    IAdministrationTime,
} from 'App/components/Medication/AdministrationPoint/AdministrationPoint';
import { IMedication } from 'App/components/Medication/Medication';
import { createSelector } from 'reselect';
import { getSchemaTimes } from 'App/utils/clientSetupManager';
import { useRemoteData } from '@cistec/common_web/hooks/useRemoteData';
import { loadMedication } from 'App/actions/medication';
import { getPatNrFromSession } from 'App/utils/sessionStorage';

const emptyList = Imm.List<string>();
const emptyMap = Imm.Map<string, any>();

/**
 * Create a set with all point in times at which medication is administered
 */
const hotspotTimes = createSelector([mediOrdersOfCurPat], getHotspotsTimes);

/**
 * Merged times of loaded hotspots and configured times for schedule "schema"
 */
const administrationTimes = createSelector([hotspotTimes], (hotspots: Imm.Set<string>) => {
    const timeRange = {
        startTime: moment().startOf('day').format(KISIM_TIME_FORMAT),
        stopTime: moment().endOf('day').format(KISIM_TIME_FORMAT),
    };

    return createAdministrationTimes(hotspots, Imm.Set<string>(), timeRange, getSchemaTimes());
});

export const activeMediOrdersOfCurPat = createSelector(
    [mediOrdersOfCurPat],
    (mediOrders: Imm.Map<string, any>): Imm.Map<string, any> => {
        return ((Imm.Map.isMap(mediOrders) && mediOrders) || emptyMap)
            .filter((order: Imm.Map<string, any>) => {
                const isReserve = order.get('PRIORITY') === 'R';
                const isDeleted = isDeletedOrder(order);
                const isPaused = isPausedOrder(order);
                return !isDeleted && !isPaused && !isReserve;
            })
            .toMap();
    }
);
/**
 * Build rows of orders which can be displayed together.
 */
export const ordersListedPerView = createSelector([activeMediOrdersOfCurPat], (orders: Imm.Map<string, any>) => {
    const orderToSuccessor = mapSubsequentOrders(orders, false, true);
    const sortedOrders = orders.sort((orderA: Imm.Map<string, any>, orderB: Imm.Map<string, any>) =>
        sortOrdersBasic(orderA, orderB)
    ) as Imm.Map<string, any>;
    return buildRowsOfOrders(sortedOrders, sortedOrders, orderToSuccessor);
});

export const medication = createSelector(
    [ordersListedPerView, administrationTimes],
    (
        ordersList: Imm.List<Imm.List<Imm.Map<string, any>>>,
        administrationTimes: IAdministrationTime[]
    ): IMedication[] => {
        const emptyApplications = Imm.Map({
            VAL: 0,
            UNIT: '',
        });

        const getHotspotAtAdminTime = (order: Imm.Map<string, any>, time: string) => {
            const hotspotAtAdminTime = order
                .get('Applications', emptyList)
                .filter((application: Imm.Map<string, any>) => application.get('DAT') === time)
                .first(emptyApplications);
            return hotspotAtAdminTime ? hotspotAtAdminTime : emptyMap;
        };

        const medication: IMedication[] = [];

        ordersList.forEach((row: Imm.List<Imm.Map<string, any>>) => {
            // these values should be the same for all orders of this row
            let paidByPatient = false;
            let fkey = '';
            let title = '';
            let administrationPoints: IAdministrationPoint[] = [];

            row.forEach((order: Imm.Map<string, any>) => {
                /**
                 * Build an AdministrationPoint based on the order
                 * @param adminTime
                 */
                const getAdministrationPointAtTime = (adminTime: IAdministrationTime) => {
                    const hotspotAtAdminTime = getHotspotAtAdminTime(order, adminTime.time);

                    const amountPrescribed = Number(hotspotAtAdminTime.get('VAL')) || 0;
                    const prescribedAmountIsActuallyUnknown = hotspotAtAdminTime.get('VAL') === '?';
                    const unitPrescribed =
                        hotspotAtAdminTime.get('UNIT') || order.getIn(['CONTENT', 'Plugin', 'UNIT']) || '';

                    return {
                        ...adminTime,
                        amountPrescribed,
                        prescribedAmountIsActuallyUnknown,
                        unitPrescribed,
                    };
                };
                // Build an administration time for each point in time at which medication is administered
                const administrationPointsOfOrder: IAdministrationPoint[] = administrationTimes
                    .map(getAdministrationPointAtTime)
                    .filter((administrationPoint: IAdministrationPoint) => {
                        if (!!administrationPoint.schemaLabel) {
                            // keep all administration points with schema schedule (these administration points have
                            // a label, others have no label).
                            return true;
                        }
                        if (
                            administrationPoint.amountPrescribed ||
                            administrationPoint.prescribedAmountIsActuallyUnknown
                        ) {
                            // keep any other administration point with a prescribed
                            return true;
                        }
                        // remove all other administration points
                        return false;
                    });
                // merge administration points of one or more orders
                if (administrationPointsOfOrder && administrationPointsOfOrder.length > 0) {
                    const orderStartTime = moment(order.get('STARTDAT'), KISIM_TIME_FORMAT);

                    administrationPoints = mergeAdministrationPoints(
                        administrationPoints,
                        administrationPointsOfOrder,
                        orderStartTime
                    );

                    paidByPatient = order.getIn(['CONTENT', 'PaidByPat']) === 'Y';
                    fkey = order.get('FKEY') || fkey || ''; // FIXME: muss gleich sein für alle?
                    title = order.get('NAME');
                }
            });

            if (!isEmpty(administrationPoints)) {
                medication.push({
                    paidByPatient,
                    administrationPoints,
                    fkey,
                    title,
                });
            }
        });
        return medication.sort((medicationA: IMedication, medicationB: IMedication) =>
            compareStringWNumber(medicationA.title, medicationB.title)
        );
    }
);

function isEmpty(administrationPoints: IAdministrationPoint[]) {
    return administrationPoints.reduce((empty: boolean, point: IAdministrationPoint) => {
        return empty && !point.amountPrescribed && !point.prescribedAmountIsActuallyUnknown;
    }, true);
}

/**
 * Merge existing administration points of one row with the administration points of a succeeding order. Existing
 * administration points are removed if the time of them are after the start of the succeeding
 * order. Vice versa succeeding administration points, which are before the start of its order, are
 * removed as well. Then all administration points are merged.
 * @param precedingAPs   Administration points which are already merged and belong to the same row.
 * @param succeedingAPs  Administration points which should be merged to the same row.
 * @param succeedingOrdersStartTime     Start time of the order to which succeedingAPs belongs
 */
function mergeAdministrationPoints(
    precedingAPs: IAdministrationPoint[],
    succeedingAPs: IAdministrationPoint[],
    succeedingOrdersStartTime: Moment
): IAdministrationPoint[] {
    /**
     * Here you create an array of administration points: at least all the schema times will be included. This is
     * necessary since it is required that all the schema times has to be displayed on the page!
     * By allowing: !!administrationPoint.schemaLabel you will create at least an initial state with all the schema
     * times
     */
    let succeedingAPsFiltered = succeedingAPs.filter((administrationPoint: IAdministrationPoint) => {
        return (
            !!administrationPoint.schemaLabel ||
            moment(administrationPoint.time, KISIM_TIME_FORMAT).isSameOrAfter(succeedingOrdersStartTime)
        );
    });

    /**
     * Filter out all administration points before the succeedingOrdersStartTime
     */
    const precedingAPsFiltered = precedingAPs.filter((administrationPoint: IAdministrationPoint) => {
        return moment(administrationPoint.time, KISIM_TIME_FORMAT).isBefore(succeedingOrdersStartTime);
    });

    /**
     * Administration points which are connected to a schema time (preceding) has to be removed from
     * succeedingAPsFiltered (remove placeholder created at the beginning)
     */
    succeedingAPsFiltered = succeedingAPsFiltered.filter((administrationPoint: IAdministrationPoint) => {
        return !precedingAPsFiltered.find(
            (precedingAP: IAdministrationPoint) => precedingAP.time === administrationPoint.time
        );
    });

    return [...precedingAPsFiltered, ...succeedingAPsFiltered].sort(
        (administrationA: IAdministrationPoint, administrationB: IAdministrationPoint) =>
            compareStringWNumber(administrationA.time, administrationB.time)
    );
}

// HOOKS

export function useMedicationData() {
    return useRemoteData({}, () => ({
        isLoading: isLoadingOrdersOfCurPat,
        select: medication,
        shouldFetch: (state, _action, extras) => {
            const patnr = getPatNrFromSession();
            return !!patnr && (!extras.selected || !extras.selected.length);
        },
        fetch: (dispatch, state) => {
            const patnr = getPatNrFromSession();
            dispatch(loadMedication(patnr));
        },
        noRefetch: true,
    }));
}
